(() => {
  // Extras for the Burrow-style product template. product.js still runs the gallery, variant picker and price.
  const formatMoney = (cents, format) => {
    const value = ((Number(cents) || 0) / 100).toFixed(2);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, `${grouped}.${fraction}`);
  };

  const init = (root) => {
    const dataEl = root.querySelector('[data-product-json]');
    if (!dataEl || root.dataset.pdpbReady) return;
    root.dataset.pdpbReady = 'true';
    const data = JSON.parse(dataEl.textContent);
    const settingsEl = root.querySelector('[data-pdpb-settings]');
    const settings = settingsEl ? JSON.parse(settingsEl.textContent) : { installments: 4 };
    const money = (cents) => formatMoney(cents, data.money_format);

    const idInput = root.querySelector('[data-variant-id]');
    const addButton = root.querySelector('[data-pdpb-add]');
    const priceWrap = root.querySelector('[data-price]');
    const percent = root.querySelector('[data-pdpb-percent]');
    const saleLabelEl = root.querySelector('[data-pdpb-sale-label]');
    const saleLabel = saleLabelEl ? saleLabelEl.innerHTML.trim() : '';
    const installment = root.querySelector('[data-pdpb-installment]');
    const planInputs = Array.from(root.querySelectorAll('[data-pdpb-plan]'));
    const protectEl = root.querySelector('[data-pdpb-protect-json]');
    const plans = protectEl ? JSON.parse(protectEl.textContent) : [];

    const currentVariant = () => data.variants.find((v) => v.id === Number(idInput.value)) || data.variants[0];

    // A plan costs a percentage of the light's price, rounded to whole dollars; each plan product has one variant per dollar amount.
    const planVariant = (index, variant) => {
      const plan = plans[index];
      if (!plan) return null;
      const target = Math.max(100, Math.round((variant.price * plan.percent) / 10000) * 100);
      return plan.variants.find((pv) => pv.price === target) || null;
    };
    const selectedPlan = (variant) => {
      const checked = planInputs.find((input) => input.checked);
      return checked && checked.value !== '' ? planVariant(Number(checked.value), variant) : null;
    };

    const render = () => {
      const v = currentVariant();
      const onSale = v.compare_at_price > v.price;
      if (priceWrap) priceWrap.classList.toggle('is-sale', onSale);
      if (percent) {
        const pct = onSale ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
        percent.textContent = onSale ? `${pct}% off${saleLabel ? ` | ${saleLabel}` : ''}` : '';
      }
      if (installment) installment.textContent = money(Math.round(v.price / (settings.installments || 4)));
      root.querySelectorAll('[data-pdpb-plan-card]').forEach((card) => {
        const pv = planVariant(Number(card.dataset.pdpbPlanCard), v);
        card.hidden = !pv;
        const input = card.querySelector('input');
        if (!pv && input.checked) planInputs[0].checked = true;
        if (pv) card.querySelector('[data-pdpb-plan-price]').textContent = `+${money(pv.price).replace(/\.00(?=\D*$)/, '')}`;
      });
      const plan = selectedPlan(v);
      if (addButton) {
        const extra = plan ? plan.price : 0;
        addButton.textContent = v.available ? `+ Add to cart - ${money(v.price + extra)}` : 'Sold out';
        addButton.disabled = !v.available;
      }
    };

    // product.js handles the change first; refresh our extras right after it.
    const picker = root.querySelector('[data-variant-picker]');
    if (picker) picker.addEventListener('change', () => setTimeout(render, 0));
    planInputs.forEach((input) => input.addEventListener('change', render));
    render();

    // Add to cart, with the protection plan as its own line when ticked.
    const form = root.querySelector('[data-pdpb-form]');
    const errorEl = root.querySelector('[data-form-error]');
    const added = root.querySelector('[data-added-message]');
    const base = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
    if (form && window.fetch) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!addButton || addButton.disabled) return;
        const v = currentVariant();
        const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]').value) || 1);
        const items = [{ id: v.id, quantity }];
        const plan = selectedPlan(v);
        if (plan) items.push({ id: plan.id, quantity, properties: { _covers: `${v.id}` } });
        addButton.classList.add('is-loading');
        addButton.disabled = true;
        if (errorEl) errorEl.hidden = true;
        try {
          const response = await fetch(`${base}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Could not add to cart.');
          const cart = await fetch(`${base}cart.js`).then((r) => r.json());
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
            el.dataset.count = cart.item_count;
          });
          if (added) {
            added.hidden = false;
            clearTimeout(added._timer);
            added._timer = setTimeout(() => { added.hidden = true; }, 6000);
          }
        } catch (error) {
          if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
        } finally {
          addButton.classList.remove('is-loading');
          render();
        }
      });
    }
  };

  // FAQ: only one answer open at a time.
  const initFaq = (scope) => {
    scope.querySelectorAll('[data-pdpb-faq]').forEach((list) => {
      list.addEventListener('toggle', (event) => {
        if (!event.target.open) return;
        list.querySelectorAll('details[open]').forEach((d) => { if (d !== event.target) d.open = false; });
      }, true);
    });
  };

  // Reviews: filter by star bar, "See more", and the Helpful button.
  const initReviews = (scope) => {
    scope.querySelectorAll('[data-pdpb-reviews]').forEach((section) => {
      if (section.dataset.pdpbReady) return;
      section.dataset.pdpbReady = 'true';
      const items = Array.from(section.querySelectorAll('.pdpb-review'));
      const list = section.querySelector('[data-review-items]');
      const pageSize = Number(list && list.dataset.pageSize) || 5;
      const more = section.querySelector('[data-review-more]');
      const note = section.querySelector('[data-review-filter-note]');
      let filter = '';
      let shown = pageSize;

      const show = () => {
        const matching = items.filter((item) => !filter || item.dataset.rating === filter);
        items.forEach((item) => { item.hidden = true; });
        matching.slice(0, shown).forEach((item) => { item.hidden = false; });
        if (more) more.hidden = matching.length <= shown;
        if (note) {
          note.hidden = !filter;
          note.querySelector('span').textContent = filter ? `Showing ${matching.length} ${filter}-star ${matching.length === 1 ? 'review' : 'reviews'}` : '';
        }
        section.querySelectorAll('.pdpb-reviews__bar').forEach((bar) => bar.classList.toggle('is-active', bar.dataset.reviewFilter === filter));
      };

      section.querySelectorAll('[data-review-filter]').forEach((button) => {
        button.addEventListener('click', () => {
          filter = button.dataset.reviewFilter === filter ? '' : button.dataset.reviewFilter;
          shown = pageSize;
          show();
          if (list) list.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
      if (more) more.addEventListener('click', () => { shown += pageSize; show(); });
      section.querySelectorAll('[data-review-helpful]').forEach((button) => {
        button.addEventListener('click', () => {
          button.hidden = true;
          button.nextElementSibling.hidden = false;
        });
      });
    });
  };

  const run = (scope) => {
    scope.querySelectorAll('[data-pdpb]').forEach(init);
    initFaq(scope);
    initReviews(scope);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document));
  else run(document);
  document.addEventListener('shopify:section:load', (event) => run(event.target));
})();
